import React, { useState, useEffect } from "react";
import { Card, Row, Col, Button, Badge, Table, Spinner, Alert, Tooltip, OverlayTrigger } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";


export default function DetailProduit() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [produit, setProduit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mouvements, setMouvements] = useState([]);

    useEffect(() => {
        fetchProduit();
        fetchMouvements();
    }, [id]);

    const fetchProduit = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/produits.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setProduit(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchMouvements = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/mouvements_stock.php?produit=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (response.ok) {
                const result = await response.json();
                setMouvements(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement mouvements", err);
        }
    };

    const getStockStatus = () => {
        if (!produit) return { label: '', color: '' };
        if (!produit.stock_actuel || produit.stock_actuel <= 0) {
            return { label: 'Rupture', color: '#ef4444', icon: 'ri-close-circle-line' };
        }
        if (produit.stock_actuel <= produit.seuil_alerte) {
            return { label: 'Critique', color: '#f59e0b', icon: 'ri-alert-line' };
        }
        return { label: 'Normal', color: '#10b981', icon: 'ri-check-line' };
    };

    const getMouvementBadge = (type) => {
        if (type === 'entree') {
            return <span className="status-badge" style={{ background: '#10b98115', color: '#10b981' }}><i className="ri-add-line me-1 fs-10"></i>Entrée</span>;
        }
        return <span className="status-badge" style={{ background: '#ef444415', color: '#ef4444' }}><i className="ri-subtract-line me-1 fs-10"></i>Sortie</span>;
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement du produit...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !produit) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Produit non trouvé</h6>
                                <p className="mb-0 small">{error || "Le produit que vous recherchez n'existe pas"}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={() => navigate('/stock/produits')}>
                                    <i className="ri-arrow-left-line me-1"></i>Retour
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const stockStatus = getStockStatus();

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-eye-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Détail du produit</h4>
                                    <p className="text-muted small mb-0">{produit.designation}</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/stock/produits')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={() => navigate(`/stock/produits/modifier/${id}`)}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                                style={{ background: '#21ACCD', border: 'none' }}
                            >
                                <i className="ri-pencil-line"></i>
                                Modifier
                            </Button>
                        </div>
                    </div>

                    <Row className="g-4">
                        {/* Colonne gauche - Informations principales */}
                        <Col lg={8}>
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                    <h5 className="mb-0 text-white">
                                        <i className="ri-information-line me-2"></i>
                                        Informations générales
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Row className="g-4">
                                        <Col md={6}>
                                            <div className="info-card p-3">
                                                <small className="text-muted d-block text-uppercase fw-semibold">Référence</small>
                                                <h5 className="mb-0">{produit.reference}</h5>
                                                {produit.code_barre && (
                                                    <small className="text-muted d-block mt-1">
                                                        <i className="ri-qr-code-line me-1"></i>
                                                        {produit.code_barre}
                                                    </small>
                                                )}
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="info-card p-3">
                                                <small className="text-muted d-block text-uppercase fw-semibold">Désignation</small>
                                                <h5 className="mb-0">{produit.designation}</h5>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="info-card p-3">
                                                <small className="text-muted d-block text-uppercase fw-semibold">Catégorie</small>
                                                <h5 className="mb-0">{produit.nom_categorie || 'Non catégorisé'}</h5>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="info-card p-3">
                                                <small className="text-muted d-block text-uppercase fw-semibold">Unité de mesure</small>
                                                <h5 className="mb-0">{produit.unite_mesure}</h5>
                                            </div>
                                        </Col>
                                        <Col md={12}>
                                            <div className="info-card p-3">
                                                <small className="text-muted d-block text-uppercase fw-semibold">Description</small>
                                                <p className="mb-0">{produit.description || 'Aucune description'}</p>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* Prix et taxes */}
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                    <h5 className="mb-0 text-white">
                                        <i className="ri-price-tag-3-line me-2"></i>
                                        Prix et taxes
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Row className="g-4">
                                        <Col md={4}>
                                            <div className="text-center p-3 bg-light rounded-3">
                                                <small className="text-muted d-block">Prix d'achat HT</small>
                                                <h4 className="text-muted mb-0">{formatCurrency(produit.prix_achat_ht)}</h4>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="text-center p-3 bg-light rounded-3">
                                                <small className="text-muted d-block">Prix de vente HT</small>
                                                <h4 className="text-primary mb-0" style={{ color: '#21ACCD' }}>{formatCurrency(produit.prix_vente_ht)}</h4>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="text-center p-3 bg-light rounded-3">
                                                <small className="text-muted d-block">Marge</small>
                                                <h4 className="text-success mb-0">{formatCurrency(produit.prix_vente_ht - produit.prix_achat_ht)}</h4>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="info-card p-3">
                                                <small className="text-muted d-block text-uppercase fw-semibold">TVA</small>
                                                <h5 className="mb-0">{produit.tva_taux}%</h5>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="info-card p-3">
                                                <small className="text-muted d-block text-uppercase fw-semibold">CSS</small>
                                                <h5 className="mb-0">{produit.css_taux}%</h5>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Colonne droite - Stock */}
                        <Col lg={4}>
                            <Card className="border-0 shadow-sm rounded-3 mb-4">
                                <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                    <h5 className="mb-0 text-white">
                                        <i className="ri-stack-line me-2"></i>
                                        État du stock
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <div className="text-center mb-4">
                                        <div className="stock-circle mx-auto mb-3" style={{ background: `${stockStatus.color}15` }}>
                                            <h1 className="display-4" style={{ color: stockStatus.color }}>
                                                {formatNumber(produit.stock_actuel || 0)}
                                            </h1>
                                        </div>
                                        <span className="status-badge" style={{ background: `${stockStatus.color}15`, color: stockStatus.color }}>
                                            <i className={`${stockStatus.icon} me-1 fs-10`}></i>
                                            {stockStatus.label}
                                        </span>
                                    </div>

                                    <hr />

                                    <Row className="g-3">
                                        <Col xs={6}>
                                            <div className="text-center p-3 bg-light rounded-3">
                                                <small className="text-muted d-block">Seuil d'alerte</small>
                                                <h5 className="mb-0">{produit.seuil_alerte}</h5>
                                            </div>
                                        </Col>
                                        <Col xs={6}>
                                            <div className="text-center p-3 bg-light rounded-3">
                                                <small className="text-muted d-block">Stock minimum</small>
                                                <h5 className="mb-0">{produit.stock_minimum}</h5>
                                            </div>
                                        </Col>
                                        <Col xs={6}>
                                            <div className="text-center p-3 bg-light rounded-3">
                                                <small className="text-muted d-block">Stock maximum</small>
                                                <h5 className="mb-0">{produit.stock_maximum || '-'}</h5>
                                            </div>
                                        </Col>
                                        <Col xs={6}>
                                            <div className="text-center p-3 bg-light rounded-3">
                                                <small className="text-muted d-block">Emplacement</small>
                                                <h5 className="mb-0">{produit.emplacement || '-'}</h5>
                                            </div>
                                        </Col>
                                    </Row>

                                    <div className="d-grid gap-2 mt-4">
                                        <OverlayTrigger placement="top" overlay={<Tooltip>Ajouter du stock</Tooltip>}>
                                            <Button 
                                                variant="success" 
                                                onClick={() => navigate(`/stock/mouvements/ajouter/${id}?type=entree`)}
                                                className="rounded-pill py-2"
                                                style={{ background: '#10b981', border: 'none' }}
                                            >
                                                <i className="ri-add-box-line me-2"></i>
                                                Ajouter au stock
                                            </Button>
                                        </OverlayTrigger>
                                        <OverlayTrigger placement="top" overlay={<Tooltip>Retirer du stock</Tooltip>}>
                                            <Button 
                                                variant="warning" 
                                                onClick={() => navigate(`/stock/mouvements/ajouter/${id}?type=sortie`)}
                                                disabled={produit.stock_actuel <= 0}
                                                className="rounded-pill py-2"
                                                style={{ background: '#f59e0b', border: 'none' }}
                                            >
                                                <i className="ri-subtract-line me-2"></i>
                                                Retirer du stock
                                            </Button>
                                        </OverlayTrigger>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Historique des mouvements */}
                        <Col lg={12}>
                            <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                                <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                    <h5 className="mb-0 text-white">
                                        <i className="ri-history-line me-2"></i>
                                        Historique des mouvements
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <div className="table-responsive">
                                        <Table hover className="align-middle mb-0 custom-table">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th className="ps-4">Date</th>
                                                    <th>Type</th>
                                                    <th className="text-center">Quantité</th>
                                                    <th className="text-center">Stock avant</th>
                                                    <th className="text-center">Stock après</th>
                                                    <th>Document</th>
                                                    <th className="pe-4">Commentaire</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {mouvements.length > 0 ? (
                                                    mouvements.map((mvt, idx) => (
                                                        <tr key={idx} className="movement-row">
                                                            <td className="ps-4"><small>{formatDate(mvt.date_mouvement)}</small></td>
                                                            <td>{getMouvementBadge(mvt.type_mouvement)}</td>
                                                            <td className="text-center fw-bold">
                                                                <span className={mvt.type_mouvement === 'entree' ? 'text-success' : 'text-danger'}>
                                                                    {mvt.type_mouvement === 'entree' ? '+' : '-'}{mvt.quantite}
                                                                </span>
                                                            </td>
                                                            <td className="text-center">{mvt.stock_avant}</td>
                                                            <td className="text-center fw-bold text-primary">{mvt.stock_apres}</td>
                                                            <td>
                                                                {mvt.reference_document && (
                                                                    <small className="text-muted">{mvt.reference_document}</small>
                                                                )}
                                                                {mvt.type_document && (
                                                                    <div><small className="text-muted">{mvt.type_document}</small></div>
                                                                )}
                                                                {!mvt.reference_document && <span className="text-muted">-</span>}
                                                            </td>
                                                            <td className="pe-4"><small className="text-muted">{mvt.commentaire || '-'}</small></td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="7" className="text-center py-5">
                                                            <i className="ri-history-line fs-48 text-muted mb-3 d-block"></i>
                                                            <h5>Aucun mouvement enregistré</h5>
                                                            <p className="text-muted">Commencez par ajouter ou retirer du stock</p>
                                                            <Button 
                                                                variant="primary" 
                                                                onClick={() => navigate(`/stock/mouvements/ajouter/${id}?type=entree`)}
                                                                className="rounded-pill"
                                                                style={{ background: '#21ACCD', border: 'none' }}
                                                            >
                                                                <i className="ri-add-box-line me-2"></i>
                                                                Premier mouvement
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </div>
                <Footer />
            </div>

            <style>{`
                .stock-circle {
                    width: 120px;
                    height: 120px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                }
                .stock-circle h1 {
                    font-size: 2.5rem;
                    font-weight: 700;
                    margin: 0;
                }
                
                .info-card {
                    background: #f8fafc;
                    border-radius: 12px;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                }
                .info-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                    border-color: #21ACCD;
                }
                
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.4rem 1rem;
                    border-radius: 30px;
                    font-size: 0.75rem;
                    font-weight: 600;
                }
                
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .movement-row {
                    transition: all 0.2s ease;
                }
                
                .movement-row:hover {
                    background: #f8fafc;
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}